import { useMemo } from 'react';
import { ContentAttributesToEvaluate, EvaluatedContentAttributes } from '../entities';
import { FlowAttributeKey, FlowAttributes } from '../entities/FlowAttributes';
import { useEvaluateConditionalAttributes } from './useEvaluateConditionalAttributes';

export const prepareContentAttributes = (
  rawAttributes: FlowAttributes[],
): ContentAttributesToEvaluate => {
  return rawAttributes.reduceRight((attributesToEvaluate, attributes) => {
    const attributesEntries = Object.entries(attributes) as Array<
      [FlowAttributeKey, FlowAttributes[FlowAttributeKey]]
    >;

    const allPresentArrayAttributes = attributesEntries.reduce(
      (reducedAttributes, [attributeKey, attributeValue]) => {
        const isArrayAttribute = Array.isArray(attributeValue);

        if (!isArrayAttribute) {
          return reducedAttributes;
        }

        return {
          ...reducedAttributes,
          [attributeKey]: attributeValue,
        };
      },
      {} as ContentAttributesToEvaluate,
    );

    return {
      ...attributesToEvaluate,
      ...allPresentArrayAttributes,
    };
  }, {} as ContentAttributesToEvaluate);
};

export const useFlowContentAttributes = (
  attributes: ContentAttributesToEvaluate,
): EvaluatedContentAttributes => {
  const evaluateConditionalFlowAttributes = useEvaluateConditionalAttributes();

  const evaluatedAttributes = useMemo(() => {
    const attributeEntries = Object.entries(attributes);

    return attributeEntries.reduce((evaluatedAttributeMap, attributeEntry) => {
      const [attributeKey, attributeValue] = attributeEntry;

      if (!attributeValue) {
        return evaluatedAttributeMap;
      }

      return {
        ...evaluatedAttributeMap,
        [attributeKey]: evaluateConditionalFlowAttributes(attributeValue),
      };
    }, {} as EvaluatedContentAttributes);
  }, [attributes, evaluateConditionalFlowAttributes]);

  return evaluatedAttributes;
};
